import { Label } from "@/components/ui/label.tsx";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip.tsx";
import { cn } from "@/lib/utils";
import {
  CircleCheck,
  FileImage,
  HelpCircleIcon,
  Upload,
  X,
} from "lucide-react";
import * as React from "react";

export interface InputProps
  extends React.InputHTMLAttributes<HTMLInputElement> {
  tootltip?: string;
  label?: string;
  sublabel?: string;
  unit?: string;
  isFileLoaded?: boolean;
  onFileRemove?: () => void;
  fileName?: string;
}

const Input = React.forwardRef<HTMLInputElement, InputProps>(
  ({ className, type, ...props }, ref) => {
    const isFileInput = type === "file";
    const isFileLoaded = props.isFileLoaded || false;

    // Component for file input
    if (isFileInput) {
      return (
        <div className="w-full">
          {props.label && (
            <div className="flex flex-col gap-1 mb-1.5">
              <Label
                htmlFor={props.id}
                className="flex text-start text-slate-700 text-sm font-medium font-['Raleway'] leading-normal"
              >
                {props.label}
              </Label>
              {props.sublabel && (
                <p className="text-primary text-[12px] font-normal font-['Raleway'] leading-normal">
                  {props.sublabel}
                </p>
              )}
            </div>
          )}

          <div className="relative group">
            <div
              className={cn(
                "relative flex flex-col items-center justify-center w-full -p-3 border-2 rounded-xl cursor-pointer from-slate-50 to-white",
                isFileLoaded
                  ? "bg-gradient-to-br"
                  : "border-slate-300 hover:border-gray-400  min-h-[140px]",

                className,
              )}
            >
              {/* hidden input */}
              <input
                id={props.id}
                type={type}
                ref={ref}
                className="absolute inset-0 w-full h-full opacity-0 cursor-pointer z-10"
                {...props}
              />

              {/* Uploaded file */}
              {isFileLoaded && props.fileName ? (
                <div className="flex items-center justify-between w-full p-4 bg-gradient-to-r from-gray-50 to-gray-100 rounded-lg border border-gray-200">
                  <div className="flex items-center space-x-3">
                    <div className="p-2 bg-gray-50">
                      <FileImage className="w-8 h-8 text-gray-400" />
                    </div>
                    <div className="flex flex-col">
                      <p className="text-sm font-medium text-gray-700 truncate max-w-[200px]">
                        {props.fileName}
                      </p>
                    </div>
                  </div>

                  <div className="flex items-center space-x-2">
                    <div className="p-1 rounded-full">
                      <CircleCheck className="w-5 h-5 text-green-500" />
                    </div>

                    {props.onFileRemove && (
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <button
                            type="button"
                            onClick={(e) => {
                              e.stopPropagation();
                              props.onFileRemove?.();
                            }}
                            className="p-1.5 rounded-full bg-red-50 hover:bg-red-100 transition-colors z-20 border border-red-100"
                          >
                            <X className="w-4 h-4 text-red-500" />
                          </button>
                        </TooltipTrigger>
                        <TooltipContent>
                          <p>Supprimer le fichier</p>
                        </TooltipContent>
                      </Tooltip>
                    )}
                  </div>
                </div>
              ) : (
                /* Whitout file */
                <div className="flex flex-col items-center space-y-4 p-6 text-center">
                  <div className="p-4 rounded-lg border border-[#CBD5E1]-600 group-hover:bg-gray-100 transition-colors">
                    <Upload className="w-6 h-6 text-gray-500" />
                  </div>

                  <div className="space-y-2">
                    <p className="text-sm font-medium text-slate-700">
                      <span className="text-blue-600 text-sm underline decoration-blue-600/30">
                        Cliquez pour importer
                      </span>{" "}
                      ou glissez et déposez
                    </p>
                    <p className="text-sm text-slate-500">
                      Formats d'images acceptés : jpg, jpeg, tif, tiff, webp,
                      png
                    </p>
                  </div>
                </div>
              )}

              {props.tootltip && (
                <Tooltip>
                  <TooltipTrigger asChild>
                    <button
                      type="button"
                      className="absolute top-3 left-3 p-1.5 rounded-full bg-white/80 hover:bg-white transition-colors z-20"
                    >
                      <HelpCircleIcon className="w-4 h-4 text-slate-500" />
                    </button>
                  </TooltipTrigger>
                  <TooltipContent>
                    <p className="text-white">{props.tootltip}</p>
                  </TooltipContent>
                </Tooltip>
              )}
            </div>
          </div>
        </div>
      );
    }

    // Input normal (non-file)
    const inputContent = (
      <div
        className={cn(
          "flex h-9 w-full justify-between rounded-md border border-input bg-white text-sm shadow-sm transition-colors file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50",
          className,
        )}
      >
        <input
          id={props.id}
          type={type}
          ref={ref}
          className={cn(
            "flex w-full bg-transparent px-3 py-1 text-sm transition-colors placeholder:text-muted-foreground focus-visible:outline-none disabled:cursor-not-allowed disabled:opacity-50",
            className,
          )}
          {...props}
        />

        <div className="flex items-center gap-2">
          {props.tootltip && (
            <Tooltip>
              <TooltipTrigger type="button" className="mr-3">
                <HelpCircleIcon className="w-5 h-5 text-slate-400" />
              </TooltipTrigger>
              <TooltipContent>
                <p className="text-white">{props.tootltip}</p>
              </TooltipContent>
            </Tooltip>
          )}

          {/* Unité */}
          {props.unit && (
            <div className="h-full text-black p-2 rounded-se-md rounded-ee-md text-md min-w-fit">
              <span className="text-black text-xs">{props.unit}</span>
            </div>
          )}
        </div>
      </div>
    );

    return props.label ? (
      <div className="w-full">
        <div className="flex flex-col gap-1 mb-1.5">
          <Label
            htmlFor={props.id}
            className="flex text-start text-slate-700 text-sm font-medium font-['Raleway'] leading-normal"
          >
            {props.label}
          </Label>
          {props.sublabel && (
            <p className="text-primary text-[12px] font-normal font-['Raleway'] leading-normal">
              {props.sublabel}
            </p>
          )}
        </div>
        {inputContent}
      </div>
    ) : (
      inputContent
    );
  },
);
Input.displayName = "Input";

export { Input };
